'use client';

/**
 * El tiempo en Jartiland — tarjeta del panel principal de Jartinet.
 *
 * Lee el MISMO pronóstico que aplica el servidor (`mod_weather/main`, la rejilla
 * de 14 días × 96 casillas de 15 min que se edita en /jartinet/admin/tiempo) y lo
 * interpreta con `@/lib/tiempo`, que es exactamente lo que hace
 * `buildServerControl()` en las Cloud Functions. Así lo que se ve aquí es lo que
 * hay dentro del juego, sin datos nuevos ni una segunda fuente que mantener.
 *
 * **AHORA vs PREVISIÓN, y por qué no se pintan igual.** El clima programado lleva
 * una probabilidad, y hay dos formas honestas de enseñarla:
 *
 * · **Ahora mismo** se resuelve (`climaResuelto`): es lo que hay en el cielo del
 *   juego en este instante, y decir otra cosa sería mentir mirando por la ventana.
 *   Debajo se anota la probabilidad con la que estaba programado.
 * · **Lo que viene** se enseña SIN resolver: icono de lo programado + su `%`,
 *   que es como funciona cualquier previsión — «lluvia, 40 %». Por eso una casilla
 *   del 20 % puede aparecer con nubarrón y luego quedarse en despejado al llegarle
 *   el turno: no es un fallo, es lo que significa un 20 %.
 *
 * Navegación: la tarjeta enseña el detalle **cuarto a cuarto** de las próximas
 * horas y 5 días; al pulsar un día se abre su detalle de 24 h **con sus cuatro
 * casillas de 15 min cada una**, y "Ver 14 días" abre la lista completa.
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { motion } from 'framer-motion';
import { CloudSun, Clock, Moon, ChevronRight, ArrowLeft, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  MINUTOS_SLOT, SLOTS_DIA, SLOTS_HORA, WEATHER_META as META, WEATHER_PESO as PESO,
  type CeldaTiempo,
  CELDA_DESPEJADA, climaResuelto, cuartosDeHora, diaDesdeFirestore, diaValido, etiquetaSlot,
  horasResumidas, madridAhora, textoProbabilidad,
} from '@/lib/tiempo';

const HORAS_RESUMEN = [10, 14, 20];   // mañana / mediodía / noche, en las filas de día
const CUARTOS_VISTA = 12;             // 3 h por delante, de 15 en 15 min
const DIAS_PANEL = 5;                 // días en la tarjeta del panel
const DIAS_MAX = 14;                  // los que guarda el admin

function sumarDias(fecha: string, n: number) {
  const d = new Date(fecha + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function nombreDia(fecha: string, hoy: string) {
  if (fecha === hoy) return 'Hoy';
  if (fecha === sumarDias(hoy, 1)) return 'Mañana';
  return new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: '2-digit' }).format(new Date(fecha + 'T12:00:00'));
}

function fechaLarga(fecha: string) {
  return new Intl.DateTimeFormat('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(fecha + 'T12:00:00'));
}

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;

// Es de noche en el juego si la hora de Madrid está fuera del día (el mod fija
// `dayTime` con esa misma hora). Solo cambia el fondo de la tarjeta.
const esDeNoche = (h: number) => h >= 21 || h < 7;

// ─── Piezas reutilizadas por la tarjeta y por los diálogos ───────────────────

/** Fila de un día: nombre + la previsión de las 10:00 / 14:00 / 20:00, con su %. */
function FilaDia({ fecha, horas, hoy, onClick }: {
  fecha: string; horas: CeldaTiempo[]; hoy: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-2 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2 text-left transition-colors hover:border-white/20 hover:bg-white/[0.06]"
    >
      <span className={cn('w-[4.5rem] shrink-0 truncate text-xs capitalize', fecha === hoy && 'font-bold text-foreground')}>
        {nombreDia(fecha, hoy)}
      </span>
      <div className="flex flex-1 items-center justify-around gap-1">
        {HORAS_RESUMEN.map((h) => {
          const c = horas[h] || CELDA_DESPEJADA;
          const I = META[c.w].icon;
          const pct = textoProbabilidad(c);
          return (
            <div key={h} className="flex flex-col items-center gap-0.5" title={`${hh(h)} · ${META[c.w].label}${pct ? ` · ${pct}` : ''}`}>
              <span className="text-[0.58rem] tabular-nums text-muted-foreground">{hh(h)}</span>
              <I className="h-4 w-4" style={{ color: META[c.w].color }} />
              {/* Hueco fijo aunque no haya % para que las tres columnas no bailen. */}
              <span className="h-[0.7rem] text-[0.55rem] font-bold leading-none tabular-nums" style={{ color: META[c.w].color }}>
                {pct}
              </span>
            </div>
          );
        })}
      </div>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
    </button>
  );
}

/**
 * Detalle de un día: las 24 horas y, dentro de cada una, **sus cuatro casillas de
 * 15 minutos** con su probabilidad. La opacidad de cada cuarto es su %, igual que
 * en la rejilla del admin: se ve dónde arrecia sin leer un solo número.
 */
function DetalleDia({ dia, slotActual }: {
  dia: CeldaTiempo[];
  /** Casilla de AHORA si el día que se está viendo es hoy; si no, `null`. */
  slotActual: number | null;
}) {
  const horas = horasResumidas(dia);
  const peor = horas.reduce<CeldaTiempo>(
    (acc, c) => (PESO[c.w] > PESO[acc.w] || (PESO[c.w] === PESO[acc.w] && c.p > acc.p) ? c : acc),
    CELDA_DESPEJADA,
  );
  const cuartosMalos = dia.filter((c) => c.w !== 'clear').length;
  const minutos = cuartosMalos * MINUTOS_SLOT;
  const P = META[peor.w];
  const IconoPeor = P.icon;

  return (
    <div className="space-y-4">
      {/* Titular del día */}
      <div className="flex items-center gap-3 rounded-2xl border border-white/8 bg-white/[0.03] p-3">
        <div
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border"
          style={{ borderColor: `${P.color}55`, background: `${P.color}1f`, color: P.color }}
        >
          <IconoPeor className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <p className="font-bold leading-tight" style={{ color: P.color }}>
            {P.label}{textoProbabilidad(peor) && <span className="ml-1.5 text-sm">hasta un {peor.p}%</span>}
          </p>
          <p className="text-xs text-muted-foreground">
            {cuartosMalos === 0
              ? 'Todo el día despejado'
              : minutos >= 60
                ? `${Math.round((minutos / 60) * 10) / 10} h con agua en la previsión`
                : `${minutos} min con agua en la previsión`}
          </p>
        </div>
      </div>

      {/* Las 24 horas, cada una con sus 4 cuartos */}
      <div className="space-y-1">
        <p className="text-[0.6rem] uppercase tracking-widest text-muted-foreground opacity-70">
          Cada {MINUTOS_SLOT} minutos
        </p>
        {Array.from({ length: 24 }, (_, h) => {
          const cuartos = cuartosDeHora(dia, h);
          const esHoraActual = slotActual !== null && Math.floor(slotActual / SLOTS_HORA) === h;
          return (
            <div
              key={h}
              className={cn(
                'flex items-center gap-2 rounded-xl border px-2 py-1.5',
                esHoraActual ? 'border-primary/50 bg-primary/10' : 'border-white/8 bg-white/[0.03]',
              )}
            >
              <span className={cn('w-10 shrink-0 text-[0.68rem] tabular-nums', esHoraActual ? 'font-bold text-primary' : 'text-muted-foreground')}>
                {hh(h)}
              </span>
              <div className="grid flex-1 grid-cols-4 gap-1">
                {cuartos.map((c, i) => {
                  const slot = h * SLOTS_HORA + i;
                  const I = META[c.w].icon;
                  const pct = textoProbabilidad(c);
                  const ahora = slot === slotActual;
                  return (
                    <div
                      key={i}
                      title={`${etiquetaSlot(slot)} · ${META[c.w].label}${pct ? ` · ${pct}` : ''}`}
                      className={cn(
                        'flex flex-col items-center gap-0.5 rounded-lg py-1',
                        ahora && 'ring-1 ring-primary/60',
                      )}
                      // La opacidad del fondo es la probabilidad, igual que en el admin.
                      style={{
                        background: c.w === 'clear'
                          ? 'rgba(255,255,255,.03)'
                          : `${META[c.w].color}${Math.round(255 * (0.08 + 0.22 * (c.p / 100))).toString(16).padStart(2, '0')}`,
                      }}
                    >
                      <span className="text-[0.55rem] leading-none tabular-nums text-muted-foreground">
                        {String((i * MINUTOS_SLOT)).padStart(2, '0')}
                      </span>
                      <I className="h-3.5 w-3.5" style={{ color: META[c.w].color }} />
                      <span className="h-[0.65rem] text-[0.55rem] font-bold leading-none tabular-nums" style={{ color: META[c.w].color }}>
                        {pct}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[0.65rem] text-muted-foreground">
        Los porcentajes son la <strong>probabilidad</strong> de que llueva en ese cuarto de hora: un
        40 % no es lluvia segura. En «ahora mismo» ya no hay porcentaje que valga — ahí se ve lo que
        de verdad está cayendo.
      </p>
    </div>
  );
}

// ─── Tarjeta ─────────────────────────────────────────────────────────────────

export function TiempoJartiland() {
  const [forecast, setForecast] = useState<Record<string, unknown> | null>(null);
  const [ahora, setAhora] = useState(() => new Date());
  const [fallo, setFallo] = useState(false);
  // Vista del diálogo: null cerrado · 'lista' los 14 días · una fecha, su detalle.
  const [vista, setVista] = useState<'lista' | string | null>(null);
  // Para que el botón "volver" del detalle sepa si venía de la lista.
  const [desdeLista, setDesdeLista] = useState(false);

  // Suscripción viva: si el admin retoca el pronóstico, la tarjeta se entera.
  useEffect(() => {
    const u = onSnapshot(
      doc(db, 'mod_weather', 'main'),
      (snap) => setForecast(((snap.exists() ? snap.data().forecast : {}) || {}) as Record<string, unknown>),
      () => setFallo(true),
    );
    return () => u();
  }, []);

  // Reloj cada 20 s: con casillas de 15 minutos, la tarjeta tiene que cambiar de
  // cuarto casi al momento (con una casilla por hora daba igual esperar más).
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 20000);
    return () => clearInterval(t);
  }, []);

  const datos = useMemo(() => {
    if (!forecast) return null;
    const { date, hour, minute, slot } = madridAhora(ahora);

    // Solo días con rejilla completa y de hoy en adelante: si el admin lleva sin
    // guardar, es mejor no enseñar días inventados como "despejado".
    // Vale tanto la rejilla nueva (96 casillas) como la vieja (24).
    const dias = Object.keys(forecast)
      .filter((f) => f >= date && diaValido(forecast[f]))
      .sort()
      .slice(0, DIAS_MAX);
    if (dias.length === 0) return null;

    // Cada día, parseado UNA vez: `celdasDia` se llama muchas veces al pintar.
    const cache = new Map<string, CeldaTiempo[]>();
    const celdasDia = (f: string): CeldaTiempo[] => {
      let d = cache.get(f);
      if (!d) { d = diaDesdeFirestore(forecast[f]); cache.set(f, d); }
      return d;
    };
    const horasDia = new Map<string, CeldaTiempo[]>();
    for (const f of dias) horasDia.set(f, horasResumidas(celdasDia(f)));

    // AHORA: resuelto (es lo que hay en el cielo), guardando su probabilidad para
    // poder anotarla debajo.
    const celdaAhora = celdasDia(date)[slot] || CELDA_DESPEJADA;
    const actual = climaResuelto(celdaAhora, date, slot);

    // LO QUE VIENE: sin resolver, que es lo que significa una previsión. Cruza la
    // medianoche sin casos especiales.
    const cuartos: { fecha: string; slot: number; celda: CeldaTiempo }[] = [];
    for (let i = 1; i <= CUARTOS_VISTA; i++) {
      const abs = slot + i;
      const fecha = sumarDias(date, Math.floor(abs / SLOTS_DIA));
      const s = abs % SLOTS_DIA;
      cuartos.push({ fecha, slot: s, celda: celdasDia(fecha)[s] || CELDA_DESPEJADA });
    }

    return {
      date, hour, minute, slot, actual, celdaAhora, cuartos, dias, celdasDia,
      horasDe: (f: string) => horasDia.get(f) || horasResumidas(celdasDia(f)),
    };
  }, [forecast, ahora]);

  // Sin permisos, sin documento o sin días guardados: la tarjeta no se pinta
  // (el panel queda como estaba, patrón de `ProcesionEnDirecto`).
  if (fallo || !datos) return null;

  const M = META[datos.actual];
  const IconoActual = M.icon;
  const noche = esDeNoche(datos.hour);
  const reloj = `${String(datos.hour).padStart(2, '0')}:${String(datos.minute).padStart(2, '0')}`;
  const verDia = (fecha: string, deLista: boolean) => { setDesdeLista(deLista); setVista(fecha); };
  // Solo tiene sentido anotar la probabilidad si era un "puede que sí".
  const notaAhora = textoProbabilidad(datos.celdaAhora);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-card/40"
      >
        {/* Ambiente: tinte del tiempo actual + azul noche */}
        <div
          className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl"
          style={{ background: M.color, opacity: 0.22 }}
        />
        {noche && <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-indigo-500/10 to-transparent" />}

        <div className="relative p-5">
          {/* Cabecera */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CloudSun className="h-4 w-4 text-sky-400" />
              <h2 className="font-bold leading-tight">El tiempo en Jartiland</h2>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/12 bg-white/8 px-2.5 py-1 text-xs">
              {noche ? <Moon className="h-3.5 w-3.5 text-indigo-300" /> : <Clock className="h-3.5 w-3.5 text-sky-400" />}
              <span className="font-bold tabular-nums">{reloj}</span>
            </div>
          </div>

          {/* Ahora mismo (resuelto: es lo que hay en el cielo) */}
          <div className="mt-4 flex items-center gap-4">
            <div
              className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl border"
              style={{ borderColor: `${M.color}55`, background: `${M.color}1f`, color: M.color }}
            >
              <IconoActual className="h-9 w-9" />
            </div>
            <div className="min-w-0">
              <p className="text-2xl font-black leading-none" style={{ color: M.color }}>{M.label}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                ahora mismo en el servidor
                {notaAhora && (
                  <span className="ml-1 opacity-80">
                    · estaba dado al {notaAhora} de {META[datos.celdaAhora.w].label.toLowerCase()}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Lo que viene, de 15 en 15 minutos */}
          <div className="mt-5">
            <p className="mb-2 text-[0.62rem] uppercase tracking-widest text-muted-foreground opacity-70">
              Próximas 3 h · cada {MINUTOS_SLOT} min
            </p>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {datos.cuartos.map(({ fecha, slot, celda }, i) => {
                const I = META[celda.w].icon;
                const pct = textoProbabilidad(celda);
                return (
                  <div
                    key={`${fecha}-${slot}-${i}`}
                    title={`${etiquetaSlot(slot)} · ${META[celda.w].label}${pct ? ` · ${pct}` : ''}`}
                    className="flex w-[42px] shrink-0 flex-col items-center gap-1 rounded-xl border border-white/8 py-2"
                    // La opacidad del fondo va con la probabilidad, como en el admin.
                    style={{
                      background: celda.w === 'clear'
                        ? 'rgba(255,255,255,.03)'
                        : `${META[celda.w].color}${Math.round(255 * (0.08 + 0.22 * (celda.p / 100))).toString(16).padStart(2, '0')}`,
                    }}
                  >
                    <span className="text-[0.58rem] tabular-nums text-muted-foreground">{etiquetaSlot(slot)}</span>
                    <I className="h-4 w-4" style={{ color: META[celda.w].color }} />
                    {/* Hueco fijo con o sin % para que la tira no dé saltos. */}
                    <span className="h-[0.7rem] text-[0.55rem] font-bold leading-none tabular-nums" style={{ color: META[celda.w].color }}>
                      {pct}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="mt-1 text-[0.6rem] text-muted-foreground opacity-80">
              El % es la <strong>probabilidad</strong> de que caiga: un 40 % no es lluvia segura.
            </p>
          </div>

          {/* Próximos días */}
          <div className="mt-4">
            <p className="mb-2 text-[0.62rem] uppercase tracking-widest text-muted-foreground opacity-70">Próximos días</p>
            <div className="space-y-1.5">
              {datos.dias.slice(0, DIAS_PANEL).map((fecha) => (
                <FilaDia key={fecha} fecha={fecha} horas={datos.horasDe(fecha)} hoy={datos.date} onClick={() => verDia(fecha, false)} />
              ))}
            </div>

            {datos.dias.length > DIAS_PANEL && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setDesdeLista(false); setVista('lista'); }}
                className="mt-2 h-8 w-full justify-center gap-1.5 text-xs"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                Ver los {datos.dias.length} días
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Diálogo: lista de días o detalle de un día */}
      <Dialog open={vista !== null} onOpenChange={(v) => { if (!v) { setVista(null); setDesdeLista(false); } }}>
        <DialogContent className="flex max-h-[90dvh] flex-col p-0 sm:max-w-2xl">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="flex items-center gap-2">
              {vista !== 'lista' && desdeLista && (
                <button
                  type="button"
                  onClick={() => setVista('lista')}
                  className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground"
                  aria-label="Volver a la lista de días"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <span className="capitalize">
                {vista === 'lista' ? `El tiempo, ${datos.dias.length} días` : vista ? fechaLarga(vista) : ''}
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Cuerpo con scroll: div plano, NO ScrollArea (gotcha [66] del CLAUDE.md) */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            {vista === 'lista' ? (
              <div className="space-y-1.5">
                {datos.dias.map((fecha) => (
                  <FilaDia key={fecha} fecha={fecha} horas={datos.horasDe(fecha)} hoy={datos.date} onClick={() => verDia(fecha, true)} />
                ))}
                <p className="pt-2 text-center text-[0.68rem] text-muted-foreground">
                  Pulsa un día para verlo de {MINUTOS_SLOT} en {MINUTOS_SLOT} minutos.
                </p>
              </div>
            ) : vista ? (
              <DetalleDia
                dia={datos.celdasDia(vista)}
                slotActual={vista === datos.date ? datos.slot : null}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
